import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Bounce, toast } from "react-toastify"
import { useSearchParams } from "react-router-dom"
import SearchInput from "./SearchInput"
import ExportLogButton from "./ExportLogButton"
import Pagination from "./Pagination"
import EmptyState from "../Common/EmptyState"
import TruncatedText from "../Common/TruncatedText"
import AdminCityListingTitle from "../AdminLayout/AdminCityListingTitle"
import { Avatar } from "../DashboardComponents/AvatarGroup"
import { ICONS } from "../../constants/icons"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import { PhotographyPreviewModal } from "../../pages/CityHub/CityHubModals"
import {
  ADMIN_PHOTOGRAPHY_LIST_WITH,
  fetchAdminPhotographyDetail,
  mapAdminPhotographyRow,
} from "../../pages/Admin/Cities/adminCityPhotographyData"

const TOAST_OPTIONS = { transition: Bounce }

/**
 * Shared Photography card grid — Admin + Manage City + City Portal.
 * GET /api/v1/admin/photography (+ optional city_id).
 */
const PhotographyListing = ({
  documentTitle = "Photography | AMOK . EARTH Admin",
  cityId: cityIdProp,
  showExport = true,
  pageSize: defaultPageSize = 10,
}) => {
  const [searchParams] = useSearchParams()
  const cityId = cityIdProp || searchParams.get("city_id") || ""

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)

  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState(null)
  const [previewPhotoId, setPreviewPhotoId] = useState(null)

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, cityId])

  const buildListParams = useCallback(
    ({ includePage = true } = {}) => {
      const params = {
        "with[]": ADMIN_PHOTOGRAPHY_LIST_WITH,
        sort_by: "created_at",
        sort_direction: "desc",
      }

      if (includePage) {
        params.page = currentPage
        params.page_size = pageSize
      }

      if (debouncedSearch) params.search = debouncedSearch
      if (cityId) params.city_id = cityId

      return params
    },
    [cityId, currentPage, pageSize, debouncedSearch],
  )

  const fetchPhotos = useCallback(async signal => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminPhotography, {
        params: buildListParams(),
        signal,
      })

      if (signal?.aborted) return

      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapAdminPhotographyRow)
        if (signal?.aborted) return
        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        const nextPageSize = result.per_page || defaultPageSize
        setPageSize(prev => (prev === nextPageSize ? prev : nextPageSize))

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total ?? mapped.length) /
              (result.per_page || defaultPageSize),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load photography")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return
      const message = toastApiError(err, "Failed to load photography")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      if (!signal?.aborted) setIsLoading(false)
    }
  }, [buildListParams, currentPage, defaultPageSize])

  useEffect(() => {
    const controller = new AbortController()
    fetchPhotos(controller.signal)
    return () => controller.abort()
  }, [fetchPhotos])

  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminPhotography, {
        params: buildListParams({ includePage: false }),
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export photography")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export photography"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, TOAST_OPTIONS)
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `photography-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Photography exported.", TOAST_OPTIONS)
    } catch (err) {
      toastApiError(err, "Failed to export photography")
    } finally {
      setIsExporting(false)
    }
  }

  const openPreview = row => {
    if (!row?.id) return
    setPreviewPhotoId(row.id)
  }

  const skeletonCards = useMemo(
    () => Array.from({ length: 10 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / pageSize) || 1),
  )

  return (
    <div className="admin-page admin-city-photography">
      <div className="admin-city-photography__toolbar">
        {cityId ? (
          <AdminCityListingTitle label="Photography" cityId={cityId} />
        ) : (
          <h1 className="admin-city-photography__title">Photography</h1>
        )}
        <div className="admin-city-photography__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search photography..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-city-photography__search"
          />
          {showExport ? (
            <ExportLogButton
              entityLabel="photography"
              onClick={handleExport}
              loading={isExporting}
            />
          ) : null}
        </div>
      </div>

      <div
        className="admin-city-photography__grid"
        aria-busy={isLoading || undefined}
      >
        {isLoading
          ? skeletonCards.map(key => (
              <article
                key={key}
                className="admin-city-photography__card admin-city-photography__card--skeleton"
              >
                <div className="admin-city-photography__skeleton admin-city-photography__skeleton--media" />
                <div className="admin-city-photography__body">
                  <div className="admin-city-photography__skeleton admin-city-photography__skeleton--title" />
                  <div className="admin-city-photography__skeleton admin-city-photography__skeleton--meta" />
                </div>
              </article>
            ))
          : rows.map(row => (
              <article
                key={row.id}
                className="admin-city-photography__card admin-city-photography__card--clickable"
                role="button"
                tabIndex={0}
                aria-label={`View ${row.title || "photography"}`}
                onClick={() => openPreview(row)}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    openPreview(row)
                  }
                }}
              >
                <div className="admin-city-photography__media">
                  {row.image ? (
                    row.isVideo ? (
                      <video
                        src={row.image}
                        className="admin-city-photography__img"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={row.image}
                        alt=""
                        className="admin-city-photography__img"
                      />
                    )
                  ) : (
                    <div className="admin-city-photography__media-empty" />
                  )}
                </div>
                <div className="admin-city-photography__body">
                  <div className="admin-city-photography__top">
                    <TruncatedText
                      as="h2"
                      className="admin-city-photography__card-title"
                    >
                      {row.title}
                    </TruncatedText>
                    <span className="admin-city-photography__date">
                      {row.date}
                    </span>
                  </div>
                  <div className="admin-city-photography__footer">
                    <div className="admin-city-photography__photographer">
                      <Avatar
                        src={row.photographer.avatar}
                        name={row.photographer.name}
                        size="xs"
                      />
                      <TruncatedText className="admin-city-photography__name">
                        {row.photographer.name}
                      </TruncatedText>
                    </div>
                    <div
                      className={`admin-city-photography__likes${
                        row.liked
                          ? " admin-city-photography__likes--liked"
                          : ""
                      }`}
                    >
                      <img
                        src={
                          row.liked ? ICONS.heartFill : ICONS.heartOutlineRed
                        }
                        alt=""
                        width={16}
                        height={16}
                      />
                      <span>{row.likes || 0}</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
      </div>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          title={error ? "Couldn’t load photography" : "No photos found"}
          description={error || "Try adjusting your search."}
          actionText={error ? "Try again" : undefined}
          onActionClick={error ? () => fetchPhotos() : undefined}
        />
      ) : null}

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="photos"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-city-photography__pagination"
      />

      <PhotographyPreviewModal
        isOpen={Boolean(previewPhotoId)}
        photographyId={previewPhotoId}
        fetchDetail={fetchAdminPhotographyDetail}
        onClose={() => setPreviewPhotoId(null)}
      />
    </div>
  )
}

export default PhotographyListing

PhotographyListing.propTypes = {
  documentTitle: PropTypes.string,
  cityId: PropTypes.string,
  showExport: PropTypes.bool,
  pageSize: PropTypes.number,
}

PhotographyListing.defaultProps = {
  documentTitle: "Photography | AMOK . EARTH Admin",
  cityId: undefined,
  showExport: true,
  pageSize: 10,
}
